import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Check, ArrowLeft } from 'lucide-react';
import Input from '../../components/Input';
import { useResetPasswordMutation } from '../../store/services/api';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);

  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  // Extract token from URL
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Password reset token is missing. Please check your email link.');
    }
  }, [searchParams]);

  const validateForm = () => {
    if (!newPassword) {
      setError('New password is required');
      return false;
    } else if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    } else if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Reset token is missing. Please check your email link.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await resetPassword({
        token,
        new_password: newPassword
      }).unwrap();
      
      setIsSuccess(true);
      toast.success('Password reset successful');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSuccess
              ? 'Your password has been reset successfully!'
              : 'Create a new password for your account'}
          </p>
        </div>

        {isSuccess ? (
          <div className="text-center bg-green-50 p-5 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-green-800 mb-2">Password Reset Complete</h3>
            <p className="text-green-600 mb-4">
              Your password has been changed successfully.
            </p>
            <p className="text-sm text-gray-600">
              Redirecting to login...
            </p>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                error={error && (!newPassword || newPassword.length < 8) ? error : ''}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Password must be at least 8 characters
              </p>
            </div>

            <div>
              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                error={error && newPassword !== confirmPassword ? error : ''}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className="w-full py-2.5 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-[#FF4444] hover:bg-[#FF3333] transition-colors disabled:bg-opacity-70 disabled:cursor-not-allowed"
              disabled={isLoading || !token}
            >
              {isLoading ? 'Resetting password...' : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="flex items-center justify-center text-sm font-medium text-gray-600 hover:text-[#FF4444] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 